// White-label accent safety (spec §4.1 "brand accent, contrast-validated").
//
// A customer supplies an arbitrary `accentHex`. We paint it onto
// `--color-primary`, which is *always* paired with `--color-primary-foreground`
// (#ffffff in both themes — see src/css/theme.css). A pale accent therefore
// produces white-on-white buttons: unreadable UI shipped straight to a paying
// tenant. This module is the guard.
//
// Deliberately dependency-free — a 40-line WCAG relative-luminance
// implementation beats pulling a colour library into the bundle for one check.

/** `--color-primary-foreground` in both light and dark (src/css/theme.css). */
export const PRIMARY_FOREGROUND_HEX = '#ffffff'

/**
 * WCAG 2.1 AA threshold for UI components / large text (1.4.11, 1.4.3).
 *
 * NOT 4.5 on purpose: the stock accent (#1B84FF) scores ~4.21:1 against
 * white, so a 4.5 gate would reject our own brand and every accent in the
 * same family. 3.0 rejects what is actually unreadable (pale yellows,
 * pastels, near-whites) without banning the palette the product ships with.
 */
export const ACCENT_CONTRAST_MIN = 3

export interface Rgb {
  r: number
  g: number
  b: number
}

/** `#abc` / `#aabbcc` (with or without `#`) → channel bytes. null if unparseable. */
export function parseHexColor(hex: string): Rgb | null {
  if (typeof hex !== 'string') return null
  const raw = hex.trim().replace(/^#/, '')
  if (!/^[0-9a-fA-F]+$/.test(raw)) return null
  let full: string
  if (raw.length === 3) {
    full = raw[0] + raw[0] + raw[1] + raw[1] + raw[2] + raw[2]
  } else if (raw.length === 6) {
    full = raw
  } else {
    return null
  }
  return {
    r: parseInt(full.slice(0, 2), 16),
    g: parseInt(full.slice(2, 4), 16),
    b: parseInt(full.slice(4, 6), 16),
  }
}

/** True when the string is a colour we can both parse and safely emit as CSS. */
export function isValidHexColor(hex: string): boolean {
  return parseHexColor(hex) !== null
}

/** WCAG 2.1 relative luminance (0 = black, 1 = white). */
export function relativeLuminance({ r, g, b }: Rgb): number {
  const channel = (v: number): number => {
    const s = v / 255
    return s <= 0.03928 ? s / 12.92 : Math.pow((s + 0.055) / 1.055, 2.4)
  }
  return 0.2126 * channel(r) + 0.7152 * channel(g) + 0.0722 * channel(b)
}

/**
 * WCAG contrast ratio between two hex colours (1 → identical, 21 → black/white).
 * Returns null when either colour can't be parsed — callers treat that as
 * "unknown", i.e. don't apply.
 */
export function contrastRatio(aHex: string, bHex: string): number | null {
  const a = parseHexColor(aHex)
  const b = parseHexColor(bHex)
  if (!a || !b) return null
  const la = relativeLuminance(a)
  const lb = relativeLuminance(b)
  const lighter = Math.max(la, lb)
  const darker = Math.min(la, lb)
  return (lighter + 0.05) / (darker + 0.05)
}

/**
 * Is `accentHex` safe to use as `--color-primary`?
 *
 * Checked against the foreground it is always paired with, so the answer
 * means "text on this surface will be legible", not "this colour is pretty".
 */
export function isAccentReadable(
  accentHex: string,
  againstHex: string = PRIMARY_FOREGROUND_HEX,
): boolean {
  const ratio = contrastRatio(accentHex, againstHex)
  return ratio !== null && ratio >= ACCENT_CONTRAST_MIN
}
